<script setup lang="ts">
/**
 * Outline panel of an image placeholder: its width and the color options the buyer picks from.
 *  Same controls as the stroke of a text (see TextColorProperties).
 */
import { computed, ref, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { useColor } from '@/composables/color';
import { colorsMapStore, useColorOptions } from '@/composables/colorOptions';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';

const COLOR_TYPE = LibraryTypeEnum.Color;
const INITIAL_OUTLINE_COLOR = '#FF0054';
// The filter scales the width with the image diagonal: 10 is already a very thick outline
const INITIAL_OUTLINE_WIDTH = 2;
const MAX_OUTLINE_WIDTH = 10;

const props = defineProps<{
    canvas: CanvasEx;
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
    cmykMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'open-outline-color-library-selector'): void;
}>();

const outlineWidth = ref(0);

// Not derived from the width: typing a 0 in the input must not collapse the section under the cursor
const isOutlineEnabled = ref(false);

watch(
    () => props.imageElement,
    (element) => {
        outlineWidth.value = element?.outlineWidth || 0;
        isOutlineEnabled.value = outlineWidth.value > 0;
    },
    { immediate: true }
);

// A single color image is traced by potrace to one flat color, which leaves nothing to outline
const disabled = computed(() => {
    const element = props.imageElement;
    return !element || element.locked || !(element.fullColor || element.grading);
});

const colors = useColorOptions<ImagePlaceholderElement>({
    store: colorsMapStore(
        { colorsMap: 'outlineColorsMap', option: 'outlineColorOption', libraryId: 'outlineColorLibraryId' },
        (element, css) => element.setOutlineColor(css)
    ),
    element: () => props.imageElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    openLibrarySelector: () => emit('open-outline-color-library-selector')
});

const { getColorFromString, cmykToDecimalString } = useColor();
const toColorObject = (color: string): ColorObject => {
    const { hex, a, cmyk } = getColorFromString(color);
    return { hex, alpha: a, cmyk: cmykToDecimalString(cmyk) };
};

const updateOutlineWidth = (value: number | string) => {
    const element = props.imageElement;
    if (!element) return;

    outlineWidth.value = Number(value) || 0;
    element.setOutlineWidth(outlineWidth.value);
    if (props.editMode) {
        element.setEditedProp('outlineWidth');
    }
    props.canvas.requestRenderAll();
};

const addOutline = () => {
    isOutlineEnabled.value = true;
    // Colors first, so the outline filter runs once and already with its color
    colors.replaceColors(new Map([[1, toColorObject(INITIAL_OUTLINE_COLOR)]]));
    updateOutlineWidth(INITIAL_OUTLINE_WIDTH);
};

const removeOutline = () => {
    isOutlineEnabled.value = false;
    updateOutlineWidth(0);
    // An empty map, never null: the backend would bring back the legacy outlineColor of the element
    colors.replaceColors(new Map());
};

/* Copy/paste color options, shared with the Fill/Stroke/Shadow/Vector sections */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

// The outline is a solid color, so a Fill clip carrying a gradient or palette can't land here
const canPasteColors = computed(() => {
    const clip = clipboard.clip();
    return !disabled.value && !!clip && !clip.hasNonSolidColor;
});

const onCopyColors = () => {
    clipboard.copy({
        colors: new Map(colors.model.colorsMap),
        libraryId: colors.model.libraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show('Outline colors copied');
};

const onPasteColors = () => {
    const clip = clipboard.clip();
    if (!canPasteColors.value || !clip) return;

    colors.replaceColors(clip.colors, clip.libraryId);
    // Pasting into an image with no outline yet would otherwise be invisible
    if (!isOutlineEnabled.value) {
        isOutlineEnabled.value = true;
        updateOutlineWidth(INITIAL_OUTLINE_WIDTH);
    }
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteImageOutline);
    toast.show('Outline colors pasted');
};
</script>

<template>
    <section class="outline-section">
        <color-options-clipboard-buttons
            title="Outline"
            :can-copy="!disabled && isOutlineEnabled"
            :can-paste="canPasteColors"
            @copy="onCopyColors"
            @paste="onPasteColors"
        />
        <div class="border-bottom" />
        <c-button
            v-if="!isOutlineEnabled"
            variant="secondary"
            text="Add Outline"
            :icon="['far', 'plus']"
            :disabled="disabled"
            @click="addOutline"
        />
        <element-library-selector
            v-else
            :disabled="disabled"
            :element-list="colors.model.colorsMap"
            :library-id="colors.model.libraryId"
            :library-type="COLOR_TYPE"
            :selected-position="colors.model.option"
            :cmyk="props.cmykMode"
            @pick-library="colors.pickColorLibrary()"
            @remove-library="colors.removeColorLibrary()"
            @option-selected="colors.onColorSelected"
            @remove-map-option="colors.removeColorOption($event)"
            @load-option="(option, value) => colors.loadColorIntoElement(option, value)"
            @options-reordered="colors.onColorOptionsReordered($event)"
            @preview-selection="colors.previewColorOption($event)"
        >
            <template #header>
                <c-input
                    :value="outlineWidth"
                    label="Width"
                    type="number"
                    :min="0"
                    :max="MAX_OUTLINE_WIDTH"
                    step="0.1"
                    :disabled="disabled"
                    @input="updateOutlineWidth"
                />
            </template>
            <template #actions>
                <c-button
                    class="remove-outline-btn"
                    variant="secondary"
                    text="Remove Outline"
                    :icon="['far', 'trash']"
                    :disabled="disabled"
                    @click="removeOutline"
                />
            </template>
        </element-library-selector>
    </section>
</template>

<style lang="scss" scoped>
.outline-section {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    .element-library-selector-wrapper {
        .remove-outline-btn {
            flex: 1 1 auto;
            order: 4;
        }
    }
}
</style>
